import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../../api';
import { Loading, ErrorState, Empty, fmtDate } from '../../components/ui';
import { Panel } from '../../components/charts';
import { CAUSE_LABEL } from './Dashboard';

/* ============================================================
   Peristiwa keterlambatan.

   Hidup di tingkat proyek, bukan di dalam satu analisis: peristiwa
   yang sama dibaca oleh beberapa analisis dengan metode berbeda.
   Kolom variation order dan NCR di tabel ini adalah bukti bahwa
   satu kejadian tidak dicatat tiga kali di tiga modul.
   ============================================================ */

type Event = {
  id: string;
  eventNo: string;
  title: string;
  startedAt: string;
  endedAt: string | null;
  cause: string;
  responsibility: string;
  _count: { variations: number; ncrs: number; attributions: number };
};

const RESP_CHIP: Record<string, string> = { OWNER: 'crit', CONTRACTOR: 'warn', NEUTRAL: '' };
const RESP_LABEL: Record<string, string> = {
  OWNER: 'Pemilik',
  CONTRACTOR: 'Kontraktor',
  NEUTRAL: 'Netral',
};

export function Events({ projectId }: { projectId: string }) {
  const [events, setEvents] = useState<Event[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    eventNo: '',
    title: '',
    description: '',
    startedAt: '',
    endedAt: '',
    cause: 'DESIGN_CHANGE',
    responsibility: 'OWNER',
  });

  const load = useCallback(async () => {
    try {
      const r = await api.get<{ events: Event[] }>(`/delay/events?projectId=${projectId}`);
      setEvents(r.events);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load delay events.');
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    if (!form.eventNo.trim() || !form.title.trim() || !form.description.trim() || !form.startedAt) return;
    setBusy(true);
    try {
      await api.post('/delay/events', {
        projectId,
        ...form,
        endedAt: form.endedAt || null,
      });
      setForm({ ...form, eventNo: '', title: '', description: '', startedAt: '', endedAt: '' });
      setNotice('Peristiwa tercatat.');
      await load();
    } catch (err) {
      setNotice(err instanceof ApiError ? err.message : 'Gagal mencatat peristiwa.');
    } finally {
      setBusy(false);
    }
  }

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!events) return <Loading rows={4} />;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Peristiwa keterlambatan</h1>
          <p>Satu peristiwa, tiga modul: yang sama bisa membawa variation order di cost. dan NCR di qa.</p>
        </div>
      </div>

      {notice && <div className="banner">{notice}</div>}

      <Panel title="Catat peristiwa" note="Uraiannya wajib — peristiwa tanpa penjelasan tidak bisa dipertahankan.">
        <form onSubmit={create}>
          <div className="filters">
            <input
              className="input"
              placeholder="Nomor — misalnya DE-01"
              value={form.eventNo}
              onChange={(e) => setForm({ ...form, eventNo: e.target.value })}
              required
            />
            <input
              className="input"
              placeholder="Judul"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
            />
            <input
              className="input"
              type="date"
              title="Mulai"
              value={form.startedAt}
              onChange={(e) => setForm({ ...form, startedAt: e.target.value })}
              required
            />
            <input
              className="input"
              type="date"
              title="Berakhir (opsional)"
              value={form.endedAt}
              onChange={(e) => setForm({ ...form, endedAt: e.target.value })}
            />
            <select
              className="select"
              value={form.cause}
              onChange={(e) => setForm({ ...form, cause: e.target.value })}
            >
              {Object.entries(CAUSE_LABEL).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
            <select
              className="select"
              value={form.responsibility}
              onChange={(e) => setForm({ ...form, responsibility: e.target.value })}
            >
              {Object.entries(RESP_LABEL).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>
          <label className="field">
            <span>Uraian</span>
            <textarea
              className="textarea"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Apa yang terjadi, dan bagaimana ia menghambat pekerjaan."
              required
            />
          </label>
          <button className="btn btn-primary" disabled={busy}>
            Catat
          </button>
        </form>
      </Panel>

      {events.length === 0 ? (
        <Empty title="Belum ada peristiwa" body="Analisis dengan metode apa pun kecuali as-planned vs as-built memerlukan setidaknya satu." />
      ) : (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>No</th>
                <th>Judul</th>
                <th>Mulai</th>
                <th>Berakhir</th>
                <th>Sebab</th>
                <th>Tanggung jawab</th>
                <th>Tertaut</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr key={e.id} className="no-click">
                  <td className="num">{e.eventNo}</td>
                  <td>{e.title}</td>
                  <td>{fmtDate(e.startedAt)}</td>
                  <td>{e.endedAt ? fmtDate(e.endedAt) : 'berlangsung'}</td>
                  <td>{CAUSE_LABEL[e.cause] ?? e.cause}</td>
                  <td>
                    <span className={`chip ${RESP_CHIP[e.responsibility] ?? ''}`}>
                      {RESP_LABEL[e.responsibility] ?? e.responsibility}
                    </span>
                  </td>
                  <td className="hint">
                    {e._count.variations > 0 && `${e._count.variations} VO`}
                    {e._count.variations > 0 && e._count.ncrs > 0 && ' · '}
                    {e._count.ncrs > 0 && `${e._count.ncrs} NCR`}
                    {e._count.variations === 0 && e._count.ncrs === 0 && '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
